import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { CatalogCard } from "../api/client";
import type { Theme } from "../theme/pixel";
import { Card } from "./Card";
import { catalogToCardView } from "../logic/catalogToCardView";
import { canAddToDeck, type Faction } from "../logic/deckBuilder";
import { factionFor } from "../theme/factions";
import { E32 } from "../theme/endesga32";
import { bevelShadow } from "../theme/styles";
import { Sigil } from "./Sigil";
import {
  stringCodec,
  stringSetCodec,
  useUrlSyncedState,
} from "../logic/useFilterState";

// Filter bar + card grid shared by Collection, DeckBuilder, and Admin.

type FactionKey = "humans" | "goblins" | "dwarves" | "orcs" | "elves";
type TypeFilter = "all" | "creature" | "spell" | "enchantment" | "trap";

export interface CardBrowserFilters {
  q: string;
  factions: string[];
  type: TypeFilter;
  sets?: string[];
}

const ALL_FACTIONS: FactionKey[] = [
  "humans",
  "goblins",
  "dwarves",
  "orcs",
  "elves",
];

export interface CardBrowserProps {
  cards: CatalogCard[];
  /** Owned copies per card id. Defaults to none-owned (Admin page). */
  owned?: Record<string, number>;
  theme: Theme;
  /** When present, each card is annotated with its current deck count. */
  deckCounts?: Record<string, number>;
  /** Hover "+" button click. Only rendered when supplied. */
  onAddCopy?: (card: CatalogCard) => void;
  onInspect?: (card: CatalogCard) => void;
  /** Hide non-owned cards entirely (Collection page). */
  ownedOnly?: boolean;
  /** DeckBuilder only — drives default faction filter and addability. */
  deckFaction?: Faction;
  /** Override the default `canAddToDeck` gate. Used by the NPC deck
   *  editor to disable copy caps / ownership / faction checks. Falls
   *  back to `canAddToDeck` when omitted. */
  addPolicy?: (
    card: CatalogCard,
    deckCount: number,
    ownedCount: number,
  ) => { ok: boolean; reason?: string };
  /** Admin's single-select inspector highlight. */
  selectedId?: string | null;
  /** Override the default badge; return undefined to fall back. */
  badgeFor?: (card: CatalogCard) => string | undefined;
  /** Appends `?v=N` to image URLs so Admin can cache-bust regenerated art. */
  imageVersionFor?: (card: CatalogCard) => number;
  extraFilter?: (card: CatalogCard) => boolean;
  /** Extra filter controls inside the shared bar (e.g. Admin pool dropdown). */
  extraFilterControl?: ReactNode;
  compareCards?: (a: CatalogCard, b: CatalogCard) => number;
  /** Mirror the built-in filters into URL search params. */
  urlSync?: boolean;
  /** Parent has already applied the built-in filters, usually server-side before pagination. */
  serverFiltered?: boolean;
  onFiltersChange?: (filters: CardBrowserFilters) => void;
  /** Cards rendered with cyan ring + glow until hovered/clicked. */
  newCardIds?: Set<string>;
  onCardSeen?: (cardId: string) => void;
  /** When true, render a set-filter group derived from the cards' `world`
   *  field. All sets selected by default. */
  enableSetFilter?: boolean;
  /** Restrict the faction picker (chips + default filter) to this list.
   *  Player-facing surfaces pass `me.unlocked_factions` here so locked
   *  factions never surface in the UI; admin surfaces omit it and get
   *  the full set. Defaults to every engine faction. Strings outside
   *  the engine's faction enum are silently dropped. */
  availableFactions?: readonly string[];
}

export function CardBrowser({
  cards,
  owned = {},
  theme,
  deckCounts,
  onAddCopy,
  onInspect,
  ownedOnly,
  deckFaction,
  addPolicy,
  selectedId,
  badgeFor,
  imageVersionFor,
  extraFilter,
  extraFilterControl,
  compareCards,
  urlSync = false,
  serverFiltered = false,
  onFiltersChange,
  newCardIds,
  onCardSeen,
  enableSetFilter = false,
  availableFactions,
}: CardBrowserProps) {
  // Visible chips + the universe of allowed faction keys. Defaults to
  // every faction when the caller doesn't restrict — admin tools want
  // the full set, players pass their unlocked factions. Filter through
  // ALL_FACTIONS so any unknown id from the server is dropped.
  const visibleFactions = useMemo<FactionKey[]>(() => {
    if (!availableFactions || availableFactions.length === 0) return ALL_FACTIONS;
    const allowed = new Set(availableFactions);
    return ALL_FACTIONS.filter((f) => allowed.has(f));
  }, [availableFactions]);

  // Decks are mono-faction; default the filter to just the deck's
  // faction (constrained by what's visible).
  const defaultFactions = useMemo<FactionKey[]>(
    () =>
      deckFaction && visibleFactions.includes(deckFaction)
        ? [deckFaction]
        : visibleFactions,
    [deckFaction, visibleFactions],
  );
  const queryCodec = useMemo(() => stringCodec<string>(""), []);
  const typeCodec = useMemo(() => stringCodec<TypeFilter>("all"), []);
  const factionsCodec = useMemo(
    () => stringSetCodec(defaultFactions),
    [defaultFactions],
  );
  const [query, setQuery] = useUrlSyncedState("q", "", urlSync, queryCodec);
  const [factionsRaw, setFactions] = useUrlSyncedState(
    "factions",
    new Set<string>(defaultFactions),
    urlSync,
    factionsCodec,
  );
  const factions = factionsRaw as Set<FactionKey>;
  const [type, setType] = useUrlSyncedState<TypeFilter>(
    "type",
    "all",
    urlSync,
    typeCodec,
  );

  // Sets present in the catalog. Filters by filesystem `pool` so the
  // chip list is "base_set / high_fantasia / npc_cards" rather than
  // the thematic `world` tag. Sorted for stable rendering. Includes
  // "(unset)" for engine-synthesised cards that have no pool.
  const allSets = useMemo<string[]>(() => {
    if (!enableSetFilter) return [];
    const seen = new Set<string>();
    for (const c of cards) seen.add(c.pool ?? "(unset)");
    return Array.from(seen).sort();
  }, [cards, enableSetFilter]);

  const setsCodec = useMemo(() => stringSetCodec(allSets), [allSets]);
  const [setsRaw, setSets] = useUrlSyncedState(
    "sets",
    new Set<string>(allSets),
    urlSync && enableSetFilter,
    setsCodec,
  );
  const sets = setsRaw as Set<string>;

  // When the available sets change (catalog finishes loading), sync
  // the selection to "all available" so a user who hasn't touched the
  // filter sees every set.
  useEffect(() => {
    if (!enableSetFilter) return;
    setSets((prev) => {
      const next = new Set<string>();
      for (const s of allSets) if (prev.has(s)) next.add(s);
      // First load: prev is empty (or stale). Default to all.
      if (next.size === 0 && prev.size === 0) {
        for (const s of allSets) next.add(s);
      } else if (next.size === 0) {
        // Previous selection no longer matches any set — default to all.
        for (const s of allSets) next.add(s);
      } else {
        // Add any newly-appeared sets so "all by default" survives reloads.
        for (const s of allSets) if (!prev.has(s) && prev.size < allSets.length) next.add(s);
      }
      return next;
    });
  }, [allSets, enableSetFilter, setSets]);

  const filterState = useMemo<CardBrowserFilters>(
    () => ({
      q: query.trim(),
      factions: Array.from(factions).sort(),
      type,
      ...(enableSetFilter ? { sets: Array.from(sets).sort() } : {}),
    }),
    [query, factions, type, sets, enableSetFilter],
  );

  useEffect(() => {
    onFiltersChange?.(filterState);
  }, [filterState, onFiltersChange]);

  const filtered = useMemo(() => {
    if (serverFiltered) return cards;
    const needle = query.trim().toLowerCase();
    return cards
      .filter((c) => {
        if (ownedOnly && !(owned[c.id] > 0)) return false;
        if (!factions.has(c.faction as FactionKey)) return false;
        if (type !== "all" && c.type !== type) return false;
        if (enableSetFilter && !sets.has(c.pool ?? "(unset)")) return false;
        if (needle) {
          const hay = (c.name + " " + (c.rules_text ?? "") + " " + (c.flavor ?? ""))
            .toLowerCase();
          if (!hay.includes(needle)) return false;
        }
        if (extraFilter && !extraFilter(c)) return false;
        return true;
      })
      .sort(compareCards ?? ((a, b) => {
        if (a.cost !== b.cost) return a.cost - b.cost;
        return a.name.localeCompare(b.name);
      }));
  }, [
    cards,
    owned,
    ownedOnly,
    factions,
    type,
    query,
    extraFilter,
    compareCards,
    serverFiltered,
    enableSetFilter,
    sets,
  ]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: "auto minmax(0, 1fr)",
        gap: 12,
        minHeight: 0,
        flex: 1,
      }}
    >
      <FilterBar
        theme={theme}
        query={query}
        onQuery={setQuery}
        factions={factions}
        onToggleFaction={(k) => {
          setFactions((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k);
            else next.add(k);
            return next;
          });
        }}
        visibleFactions={visibleFactions}
        type={type}
        onType={setType}
        sets={enableSetFilter ? sets : null}
        allSets={allSets}
        onToggleSet={(s) => {
          setSets((prev) => {
            const next = new Set(prev);
            if (next.has(s)) next.delete(s);
            else next.add(s);
            return next;
          });
        }}
        extraFilterControl={extraFilterControl}
      />
      {filtered.length === 0 ? (
        <div style={{ color: theme.inkFaint, padding: 16 }}>
          No cards match this filter.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, 180px)",
            gridAutoRows: "min-content",
            alignItems: "start",
            justifyContent: "center",
            gap: 14,
            overflowY: "auto",
            paddingRight: 4,
            paddingBottom: 12,
          }}
        >
          {filtered.map((c) => {
            const ownedCount = owned[c.id] ?? 0;
            const deckCount = deckCounts?.[c.id] ?? 0;
            const addability = deckCounts
              ? addPolicy
                ? addPolicy(c, deckCount, ownedCount)
                : canAddToDeck({
                    card: c,
                    faction: deckFaction ?? "",
                    deckCount,
                    owned: ownedCount,
                  })
              : { ok: true as const };
            const cap = c.rarity === "legendary" ? 1 : 2;
            const defaultBadge = deckCounts
              ? `${deckCount}/${Math.min(cap, ownedCount)}`
              : ownedCount > 0
                ? `×${ownedCount}`
                : undefined;
            const badge = badgeFor ? (badgeFor(c) ?? defaultBadge) : defaultBadge;
            const dim =
              (ownedOnly && ownedCount === 0) ||
              (deckCounts !== undefined && !addability.ok);
            const isNew = newCardIds?.has(c.id) === true;
            return (
              <CardTile
                key={c.id}
                card={c}
                theme={theme}
                dim={dim}
                badge={badge}
                selected={selectedId === c.id}
                imageVersion={imageVersionFor?.(c)}
                canAdd={addability.ok}
                addReason={addability.reason}
                showAddButton={!!onAddCopy && !!deckCounts}
                isNew={isNew}
                onSeen={onCardSeen}
                onInspect={() => onInspect?.(c)}
                onAdd={() => onAddCopy?.(c)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function CardTile({
  card,
  theme,
  dim,
  badge,
  selected,
  imageVersion,
  canAdd,
  addReason,
  showAddButton,
  isNew,
  onSeen,
  onInspect,
  onAdd,
}: {
  card: CatalogCard;
  theme: Theme;
  dim: boolean;
  badge: string | undefined;
  selected: boolean;
  imageVersion: number | undefined;
  canAdd: boolean;
  addReason: string | undefined;
  showAddButton: boolean;
  isNew: boolean;
  onSeen?: (cardId: string) => void;
  onInspect: () => void;
  onAdd: () => void;
}) {
  const [hover, setHover] = useState(false);
  // Card's outer ring slot is reserved for the target picker, so the
  // cyan "new in collection" ring is a separate decorative overlay.
  const NEW_COLOR = E32.cyan;
  const snapshot = useMemo(() => {
    const base = catalogToCardView(card);
    if (imageVersion && imageVersion > 0 && base.image_url) {
      return { ...base, image_url: `${base.image_url}?v=${imageVersion}` };
    }
    return base;
  }, [card, imageVersion]);
  return (
    <div
      onMouseEnter={() => {
        setHover(true);
        if (isNew) onSeen?.(card.id);
      }}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        width: 180,
      }}
      title={!canAdd && showAddButton ? addReason : undefined}
    >
      <Card
        card={snapshot}
        size="normal"
        theme={theme}
        dim={dim}
        selected={selected}
        badge={badge}
        onClick={() => {
          if (isNew) onSeen?.(card.id);
          onInspect();
        }}
      />
      {isNew && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            border: `2px solid ${NEW_COLOR}`,
            boxShadow: `0 0 12px ${NEW_COLOR}`,
            pointerEvents: "none",
            zIndex: 5,
          }}
        />
      )}
      {isNew && (
        <div
          style={{
            position: "absolute",
            top: -6,
            left: -6,
            background: NEW_COLOR,
            color: theme.bgDeep,
            fontFamily: theme.fontDisplay,
            fontSize: 9,
            padding: "2px 6px",
            border: `2px solid ${theme.bgDeep}`,
            zIndex: 6,
            pointerEvents: "none",
          }}
        >
          NEW
        </div>
      )}
      {showAddButton && hover && canAdd && (
        <button
          onClick={(ev) => {
            ev.stopPropagation();
            onAdd();
          }}
          title="Add one copy"
          style={{
            position: "absolute",
            right: 8,
            bottom: 28,
            width: 34,
            height: 34,
            borderRadius: 0,
            fontFamily: theme.fontDisplay,
            fontSize: 18,
            lineHeight: 1,
            background: theme.accent,
            color: theme.bgDeep,
            border: `2px solid ${theme.bgDeep}`,
            cursor: "pointer",
            boxShadow: `2px 2px 0 0 ${theme.bgDeep}`,
            zIndex: 6,
          }}
        >
          +
        </button>
      )}
    </div>
  );
}

function FilterBar({
  theme,
  query,
  onQuery,
  factions,
  onToggleFaction,
  visibleFactions,
  type,
  onType,
  sets,
  allSets,
  onToggleSet,
  extraFilterControl,
}: {
  theme: Theme;
  query: string;
  onQuery: (v: string) => void;
  factions: Set<FactionKey>;
  onToggleFaction: (k: FactionKey) => void;
  visibleFactions: FactionKey[];
  type: TypeFilter;
  onType: (v: TypeFilter) => void;
  sets: Set<string> | null;
  allSets: string[];
  onToggleSet: (s: string) => void;
  extraFilterControl?: ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 10,
        padding: 10,
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
        boxShadow: bevelShadow,
      }}
    >
      <input
        type="text"
        placeholder="Search by name, rules, or flavor"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        style={{
          flex: 1,
          minWidth: 200,
          padding: "8px 10px",
          fontFamily: theme.fontBody,
          fontSize: 15,
          background: theme.panelSolid,
          color: theme.ink,
          border: `2px solid ${theme.panelEdge}`,
        }}
      />
      <FactionCheckboxes
        theme={theme}
        factions={factions}
        onToggle={onToggleFaction}
        visibleFactions={visibleFactions}
      />
      <Select
        theme={theme}
        label="Type"
        value={type}
        onChange={(v) => onType(v as TypeFilter)}
        options={[
          ["all", "All types"],
          ["creature", "Creature"],
          ["spell", "Spell"],
          ["enchantment", "Enchantment"],
          ["trap", "Trap"],
        ]}
      />
      {sets && allSets.length > 0 && (
        <SetCheckboxes
          theme={theme}
          sets={sets}
          allSets={allSets}
          onToggle={onToggleSet}
        />
      )}
      {extraFilterControl}
    </div>
  );
}

function SetCheckboxes({
  theme,
  sets,
  allSets,
  onToggle,
}: {
  theme: Theme;
  sets: Set<string>;
  allSets: string[];
  onToggle: (s: string) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Set filter"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 4,
      }}
    >
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
          marginRight: 2,
        }}
      >
        SET
      </span>
      {allSets.map((s) => {
        const active = sets.has(s);
        return (
          <button
            key={s}
            type="button"
            onClick={() => onToggle(s)}
            aria-pressed={active}
            title={s}
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              padding: "6px 8px",
              background: active ? theme.accent : theme.panelSolid,
              color: active ? theme.bgDeep : theme.inkFaint,
              border: `2px solid ${active ? theme.accent : theme.panelEdge}`,
              cursor: "pointer",
            }}
          >
            {s.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

export { Select as CardBrowserSelect };

function FactionCheckboxes({
  theme,
  factions,
  onToggle,
  visibleFactions,
}: {
  theme: Theme;
  factions: Set<FactionKey>;
  onToggle: (k: FactionKey) => void;
  visibleFactions: FactionKey[];
}) {
  return (
    <div
      role="group"
      aria-label="Faction filter"
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 4,
      }}
    >
      {visibleFactions.map((k) => {
        const active = factions.has(k);
        const fc = factionFor(k);
        return (
          <button
            key={k}
            type="button"
            onClick={() => onToggle(k)}
            aria-pressed={active}
            title={fc.name}
            style={{
              fontFamily: theme.fontDisplay,
              fontSize: 10,
              letterSpacing: theme.letterSpacing,
              padding: "6px 8px",
              background: active ? fc.color : theme.panelSolid,
              color: active ? fc.ink : theme.inkFaint,
              border: `2px solid ${active ? fc.color : theme.panelEdge}`,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Sigil
              faction={k}
              size={14}
              color={active ? fc.ink : fc.sigilInk}
            />
            {fc.name.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

function Select({
  theme,
  label,
  value,
  onChange,
  options,
}: {
  theme: Theme;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          color: theme.inkFaint,
        }}
      >
        {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "6px 8px",
          fontFamily: theme.fontBody,
          fontSize: 14,
          background: theme.panelSolid,
          color: theme.ink,
          border: `2px solid ${theme.panelEdge}`,
        }}
      >
        {options.map(([k, v]) => (
          <option key={k} value={k}>
            {v}
          </option>
        ))}
      </select>
    </label>
  );
}
